import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { formatShortDay } from './presaveDates';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole panel; each part uses its own slice of it */
import styles from './PresavePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PresaveTimelineProps {
  /** When the artist opened the campaign, ISO 8601. */
  startedAt: string;
  releaseDate: string;
}

/** Nearer than this to an end and the "today" marker anchors there instead. */
const COLLISION_MARGIN = 0.15;

/**
 * The campaign as a window rather than a date: opened here, today there, out
 * then. It answers the only question the date alone cannot — how much runway
 * is left to promote the thing.
 *
 * The three labels sit on one hairline, so they collide as "today" approaches
 * either end. Past the margin the moving marker takes the end's anchor and the
 * fixed label steps aside, which is why the last days read "Today · 29 Aug".
 */
const PresaveTimelineComponent: React.FC<PresaveTimelineProps> = ({ startedAt, releaseDate }) => {
  const intl = useIntl();

  const { fraction, atStart, atEnd } = useMemo(() => {
    const start = new Date(startedAt).getTime();
    const end = new Date(releaseDate).getTime();
    const span = end - start;
    const elapsed = span > 0 ? (Date.now() - start) / span : 1;
    const clamped = Math.min(1, Math.max(0, elapsed));
    return {
      fraction: clamped,
      atStart: clamped <= COLLISION_MARGIN,
      atEnd: clamped >= 1 - COLLISION_MARGIN,
    };
  }, [releaseDate, startedAt]);

  const todayLabel = intl.formatMessage({ id: 'app.sml.presave.panel.timeline.today', defaultMessage: 'Today' });

  return (
    <div className={styles.timeline}>
      {!atStart && (
        <span className={clsx(styles.marker, styles.markerStart)} style={{ left: 0 }}>
          {formatShortDay(intl, startedAt)}
        </span>
      )}
      <span
        className={clsx(styles.marker, styles.markerNow, atStart && styles.markerStart, atEnd && styles.markerEnd)}
        style={{ left: `${fraction * 100}%` }}
      >
        {atStart && `${todayLabel} · ${formatShortDay(intl, startedAt)}`}
        {atEnd && `${todayLabel} · ${formatShortDay(intl, releaseDate)}`}
        {!atStart && !atEnd && todayLabel}
      </span>
      {!atEnd && (
        <span className={clsx(styles.marker, styles.markerEnd)} style={{ left: '100%' }}>
          {formatShortDay(intl, releaseDate)}
        </span>
      )}
      <div className={styles.timelineTrack}>
        <i className={styles.timelineFill} style={{ width: `${fraction * 100}%` }} />
      </div>
    </div>
  );
};

export const PresaveTimeline = TypedMemo(PresaveTimelineComponent);
